import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import Button, { Color, Size } from 'lib/Button';
import downloadSvg from 'assets/icons/download.svg';
import usePendoTracking from 'customHooks/usePendoTracking';
import { DropdownGroupBy } from 'components/common/Filters/DropdownGroupBy';
import { DirectSalesStatusOption } from 'components/common/types/DirectSalesCampaign.types';
import {
  allocationMetricOptions,
  deliveredMetricOptions,
  metricOptions,
} from 'components/common/DrilldownReports/consts';
import Checkbox from 'lib/Checkbox';
import { isLineLiveOrEnded } from 'utils/isLineLiveOrEnded';
import { AllocationReportProps, SelectedLine } from './AllocationReport.types';
import { DropdownDealLine } from './Filters/DropdownDealLine';
import { DropdownMetric } from './Filters/DropdownMetric';
import { useAllocationReport } from './useAllocationReport';
import { AllocationReportTable } from './Table/AllocationReportTable';
import { DirectSalesReportTypeButtons } from './ReportTypeButtons/DirectSalesReportTypeButtons';
import { ProgrammaticReportTypeButtons } from './ReportTypeButtons/ProgrammaticReportTypeButtons';
import { Store } from '../types/Store.types';

/* eslint-disable sonarjs/cognitive-complexity */
const AllocationReport: React.FC<AllocationReportProps> = ({
  hasFramesReportEnabled,
  defaultSelectedLine,
  changeReportSwitch,
  dealLines,
  isDirectSales,
  publicToken,
  publicLocaleCode,
  availableMetricOptions = [],
  availableGroupByOptions,
  isSelectAllDefault = false,
  isSelectAllLocked = false,
  isSelectHidden = false,
}) => {
  const isDeliveredAllocationMetricsEnabled = useSelector((state: Store) => state.publisher.deliveredAllocationMetrics);

  const areDeliveredMetricsAvailable = useMemo(() => {
    return (
      isDeliveredAllocationMetricsEnabled &&
      dealLines.some(({ lineStatus }) => lineStatus && isLineLiveOrEnded(lineStatus))
    );
  }, [dealLines]);

  const allocationReportMetricOptions = useMemo(() => {
    if (availableMetricOptions.length > 0) {
      return availableMetricOptions;
    }

    if (!isDirectSales) {
      return metricOptions;
    }

    if (areDeliveredMetricsAvailable) {
      return [...allocationMetricOptions, ...deliveredMetricOptions];
    }

    return allocationMetricOptions;
  }, [availableMetricOptions, isDirectSales, areDeliveredMetricsAvailable]);

  const {
    activeReportType,
    allocationReportTableParams: { gridOptions, onGridReady, rowData },
    handleChangeLine,
    handleChangeMetricType,
    handleExportReport,
    handleFetchAllocationReport,
    isDirectSalesCampaignType,
    selectedLines,
    selectedMetricType,
    selectedRowGroupColumns,
    handleToggleRowGroupColumn,
    rowGroupColumns,
    reportDataError,
  } = useAllocationReport({
    defaultSelectedLine,
    dealLines,
    isDirectSales,
    publicToken,
    publicLocaleCode,
    availableMetricOptions: allocationReportMetricOptions,
    availableGroupByOptions,
    isSelectAllDefault,
    isSelectHidden,
    isSelectAllLocked,
  });

  const { getPendoDataId } = usePendoTracking();

  const isMultiSelectEnabled = isDirectSalesCampaignType && dealLines.length > 1;
  const selectableDealLines = useMemo(
    () =>
      isMultiSelectEnabled
        ? dealLines.filter(({ lineStatus }) => lineStatus !== DirectSalesStatusOption.CANCELLED)
        : dealLines,
    [dealLines, isMultiSelectEnabled],
  );
  const [allLinesSelected, setAllLinesSelected] = useState(false);

  useEffect(() => {
    if (!isMultiSelectEnabled) return;

    setAllLinesSelected(selectedLines.length === selectableDealLines.length);
  }, [isMultiSelectEnabled, selectableDealLines.length, selectedLines.length]);

  const handleToggleSelectAll = (): void => {
    if (isSelectAllLocked) return;

    const nextValue = !allLinesSelected;
    setAllLinesSelected(nextValue);

    if (nextValue) {
      handleChangeLine('lines', selectableDealLines);
    } else {
      handleChangeLine('lines', defaultSelectedLine);
    }
  };

  const handleLineChange = (_: string, newSelection: SelectedLine | SelectedLine[]): void => {
    if (isSelectAllLocked) return;

    const lines = Array.isArray(newSelection) ? newSelection : [newSelection];

    if (lines.length === 0) return;

    handleChangeLine('lines', lines);
    setAllLinesSelected(lines.length === selectableDealLines.length);
  };

  const handleResetSelectedLines = (): void => {
    if (isSelectAllLocked) return;

    setAllLinesSelected(false);
    handleChangeLine('lines', defaultSelectedLine);
  };

  const renderReportTypeButtons = (): JSX.Element => {
    return isDirectSalesCampaignType ? (
      <DirectSalesReportTypeButtons
        activeReportType={activeReportType}
        handleFetchDirectSalesAllocationReport={handleFetchAllocationReport}
        selectedLine={selectedLines[0]}
      />
    ) : (
      <ProgrammaticReportTypeButtons
        activeReportType={activeReportType}
        handleFetchAllocationReport={handleFetchAllocationReport}
        selectedLine={selectedLines[0]}
      />
    );
  };

  return (
    <div
      className={cx('flex flex-col bg-white', {
        'h-full': !publicToken,
      })}
    >
      <header className="flex flex-col gap-3 p-4 border-b border-neutral-950-opacity-10 mb-2">
        {!publicToken && renderReportTypeButtons()}
        <div className="flex flex-col flex-1 items-center gap-4 sm:flex-row sm:items-end sm:gap-3">
          {hasFramesReportEnabled && changeReportSwitch}
          {!isSelectHidden && (
            <>
              <DropdownDealLine
                dealLines={dealLines}
                handleChangeLine={handleLineChange}
                selectedLines={selectedLines}
                isLineStatusShown
                isMultiSelectLine={isMultiSelectEnabled}
                resetSelectedLines={handleResetSelectedLines}
                isDisabled={isSelectAllLocked}
              />
              {isMultiSelectEnabled && (
                <div
                  role="presentation"
                  className={cx('flex items-center gap-2 self-center pt-6', {
                    'opacity-50 cursor-not-allowed': isSelectAllLocked,
                    'cursor-pointer': !isSelectAllLocked,
                  })}
                  onClick={handleToggleSelectAll}
                >
                  <Checkbox
                    isSelected={allLinesSelected}
                    isDisabled={isSelectAllLocked}
                    dataTestId="allocation-report-select-all"
                  />
                  <p className="body-base font-medium select-none">Select all</p>
                </div>
              )}
            </>
          )}
          {rowGroupColumns.length > 0 && (
            <DropdownGroupBy
              handleToggleRowGroupColumn={handleToggleRowGroupColumn}
              rowGroupColumns={rowGroupColumns}
              selectedRowGroupColumns={selectedRowGroupColumns}
              reportType="Allocation"
            />
          )}
          <DropdownMetric
            handleChangeMetricType={handleChangeMetricType}
            selectedMetricType={selectedMetricType}
            availableMetricOptions={allocationReportMetricOptions}
          />
          {!publicToken && (
            <Button
              classNames="ml-auto"
              svg={downloadSvg}
              label="Export report"
              size={Size.LARGE}
              color={Color.TRANSPARENT}
              onClick={handleExportReport}
              ddActionName="Allocation report / Export button"
              dataPendoId={getPendoDataId('Allocation export button')}
            />
          )}
        </div>
      </header>
      <div className={cx({ 'px-4 pb-4': publicToken })}>
        <AllocationReportTable
          gridOptions={gridOptions}
          onGridReady={onGridReady}
          rowData={rowData}
          reportDataError={reportDataError}
        />
      </div>
    </div>
  );
};
/* eslint-enable sonarjs/cognitive-complexity */

export default AllocationReport;
